/** Settings, paytable, and sound toggle menu */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { AudioManager } from '../audio/AudioManager';
import { Logger } from '../utils/Logger';

const logger = new Logger('MenuButton');

/** Menu button that opens a settings overlay with sound toggle and paytable access */
export class MenuButton extends Container {
  private _isOpen: boolean = false;
  private _overlay: Container | null = null;
  private _btnBg: Graphics;
  private _onOpenPaytable: (() => void) | null = null;

  // Design dimensions for overlay positioning
  private _designW: number;
  private _designH: number;

  constructor(designW: number = 1920, designH: number = 1080) {
    super();
    this._designW = designW;
    this._designH = designH;

    this.eventMode = 'static';
    this.cursor = 'pointer';

    // Button: 70x70 rounded rectangle with dark fill and gold/amber border
    this._btnBg = new Graphics();
    this._btnBg.roundRect(-35, -35, 70, 70, 10);
    this._btnBg.fill({ color: 0x1a1a2e });
    this._btnBg.stroke({ color: 0xd4a030, width: 2 });
    this.addChild(this._btnBg);

    // Three horizontal white lines (hamburger icon) centered in the 70x70 button
    // Button spans y: -35 to +35. Lines at y offsets -14, 0, +14 centered
    const lineW = 34;
    const lineH = 4;
    const lineX = -lineW / 2;
    const lineOffsets = [-14, 0, 14];
    const lines = new Graphics();
    for (const yOff of lineOffsets) {
      lines.rect(lineX, yOff - lineH / 2, lineW, lineH);
    }
    lines.fill({ color: 0xffffff });
    this.addChild(lines);

    this.on('pointerdown', this._handlePress, this);

    logger.info('MenuButton created (70x70) with hamburger icon');
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  /** Set callback for opening paytable */
  onOpenPaytable(callback: () => void): void {
    this._onOpenPaytable = callback;
  }

  /** Toggle the menu overlay */
  toggle(): void {
    this._isOpen = !this._isOpen;
    if (this._isOpen) {
      this._showOverlay();
    } else {
      this._hideOverlay();
    }
  }

  /** Close the menu if open */
  close(): void {
    if (this._isOpen) {
      this._isOpen = false;
      this._hideOverlay();
    }
  }

  private _handlePress(): void {
    this.toggle();
  }

  private _showOverlay(): void {
    if (this._overlay) return;

    const overlay = new Container();
    overlay.eventMode = 'static';

    // Background panel
    const panel = new Graphics();
    panel.roundRect(0, 0, 220, 200, 12);
    panel.fill({ color: 0x1a1a3a, alpha: 0.95 });
    panel.stroke({ color: 0x4444aa, width: 2 });
    overlay.addChild(panel);

    // Sound toggle button
    const audioMgr = AudioManager.getInstance();
    const soundBtn = this._createMenuOption(
      audioMgr.isMuted ? 'Sound: OFF' : 'Sound: ON',
      20,
      20,
      () => {
        const nowMuted = !audioMgr.isMuted;
        audioMgr.setMuted(nowMuted);
        soundLabel.text = nowMuted ? 'Sound: OFF' : 'Sound: ON';
        logger.info(`Sound toggled: ${nowMuted ? 'OFF' : 'ON'}`);
      },
    );
    const soundLabel = soundBtn.getChildAt(1) as Text;
    overlay.addChild(soundBtn);

    // Paytable button
    const paytableBtn = this._createMenuOption('Paytable', 20, 80, () => {
      this.close();
      if (this._onOpenPaytable) {
        this._onOpenPaytable();
      }
    });
    overlay.addChild(paytableBtn);

    // Close button
    const closeBtn = this._createMenuOption('Close', 20, 140, () => {
      this.close();
    });
    overlay.addChild(closeBtn);

    // Position relative to menu button (to the left and below)
    overlay.x = -230;
    overlay.y = 30;

    this._overlay = overlay;
    this.addChild(overlay);
  }

  private _hideOverlay(): void {
    if (this._overlay) {
      this.removeChild(this._overlay);
      this._overlay.destroy({ children: true });
      this._overlay = null;
    }
  }

  private _createMenuOption(
    label: string,
    x: number,
    y: number,
    onClick: () => void,
  ): Container {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.x = x;
    container.y = y;

    const bg = new Graphics();
    bg.roundRect(0, 0, 180, 45, 8);
    bg.fill({ color: 0x2a2a5a });
    bg.stroke({ color: 0x5555bb, width: 1 });
    container.addChild(bg);

    const text = new Text({
      text: label,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 18,
        fill: 0xffffff,
      }),
    });
    text.x = 15;
    text.y = 12;
    container.addChild(text);

    container.on('pointerdown', (e) => {
      e.stopPropagation();
      onClick();
    });

    return container;
  }

}
